import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import { ReactComponent as InfoSvg } from '@pkg/assets/icons/v2/info.svg';
import { styledTypography } from '@pkg/primitives';

import { ReactComponent as EthSvg } from '@app/pages/NFTDetailView/assets/icons/eth.svg';
import CryingFace from '@app/pages/NFTDetailView/assets/images/crying-face.png';

export const Wrapper = styled.div`
    position: relative;

    background: rgba(255, 255, 255, 0.05);
    border-radius: 15px;
`;

export const DefaultCard = styled.div<{ $isVisible: boolean }>`
    display: grid;
    grid-template-areas:
        'label label label'
        'number number number'
        'valuegroup valuegroup valuegroup'
        'timer timer timer';
    grid-template-rows: auto;
    grid-template-columns: auto auto 1fr;
    gap: 10px;

    padding: 20px;

    transition:
        opacity 200ms ease-in-out,
        transform 200ms ease-in-out;

    ${({ $isVisible }) =>
        !$isVisible &&
        css`
            opacity: 0;
            pointer-events: none;
            gap: 0;
            transform: scale(0.95);
        `}
`;

export const Label = styled.div`
    display: flex;
    grid-area: label;
    font-weight: 700;
    font-size: 12px;
    line-height: 12px;
    text-transform: uppercase;
    letter-spacing: -0.03em;
    color: ${({ theme }) => theme.shades.white[50]};
`;

export const InfoIcon = styled(InfoSvg)`
    display: inline;
    color: ${({ theme }) => theme.shades.white[30]};
    cursor: pointer;

    position: relative;
    top: -2px;

    transition: color ${({ theme }) => `${theme.transitions.speeds.fast}s ${theme.transitions.easesCss.cubic}`};

    width: 12px;
    height: 12px;

    margin-left: 4px;

    &:hover {
        color: ${({ theme }) => theme.shades.white[80]};
    }
`;

const EthIcon = styled(EthSvg)`
    position: relative;
    top: -2px;
`;

export const Number = styled(motion.div)`
    grid-area: number;
    font-family: ${styledTypography.allianceFontStack};
    font-style: normal;
    font-weight: 700;
    font-size: 30px;
    line-height: 30px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 2px;
`;

export const ValueGroup = styled(motion.div)`
    grid-area: valuegroup;
    display: flex;
    row-gap: 10px;
    column-gap: 4px;
    align-items: center;
    justify-content: flex-start;
`;

export const Price = styled.div`
    max-width: 100px;
    width: max-content;
    overflow: hidden;
    text-overflow: ellipsis;
    grid-area: value;
    font-weight: 700;
    font-size: 18px;
    display: flex;
    line-height: 18px;
    letter-spacing: -0.02em;
    color: rgba(255, 255, 255, 0.5);
`;

export const TimerGroup = styled.div`
    grid-area: timer;
    display: flex;
    align-items: center;

    gap: 5px;
`;

export const Time = styled.div<{ $compact?: boolean }>`
    font-weight: 700;
    font-size: ${({ $compact }) => ($compact ? 10 : 12)}px;
    line-height: 100%;
    text-align: center;
    letter-spacing: -0.03em;

    display: flex;
    align-items: baseline;
    font-variant-numeric: tabular-nums;

    span {
        color: ${({ $compact }) => ($compact ? 'white' : '#d4d4d5')};
        font-size: 10px;
        margin-left: 1px;
        margin-right: 3px;
        position: relative;
    }
`;

/* Border Effect
----------------------------------*/

const animatedgradient = keyframes`
    0% {
        background-position: 0% 0%;
    }
    100% {
        background-position: 100% 50%;
    }
`;

export const BorderEffect = styled.div<{ $color: 'yellow' | 'red' | 'purple' }>`
    border-radius: 15px;

    position: absolute;
    z-index: -1;
    inset: 0;

    padding: 2px; // stroke width

    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;

    background-image: radial-gradient(
            46.5% 46% at 0% 57%,
            #eebc4e 0%,
            rgba(235, 163, 61, 0.75) 25%,
            rgba(234, 136, 43, 0.5) 50%,
            rgba(255, 0, 0, 0) 100%
        ),
        radial-gradient(
            57.5% 76% at 100% 0%,
            #ff8a00 0%,
            rgba(252, 120, 0, 0.75) 25%,
            rgba(250, 100, 0, 0.5) 50%,
            rgba(255, 0, 0, 0) 100%
        );

    background-size: 200% 200%;
    background-position: 0% 0%;

    animation: ${animatedgradient} 2.5s ease-in-out alternate infinite;

    ${({ $color }) => {
        switch ($color) {
            case 'yellow':
                return css`
                    background-image: radial-gradient(
                            46.5% 46% at 0% 57%,
                            #eebc4e 0%,
                            rgba(235, 163, 61, 0.75) 25%,
                            rgba(234, 136, 43, 0.5) 50%,
                            rgba(255, 0, 0, 0) 100%
                        ),
                        radial-gradient(
                            57.5% 76% at 100% 0%,
                            #ff8a00 0%,
                            rgba(252, 120, 0, 0.75) 25%,
                            rgba(250, 100, 0, 0.5) 50%,
                            rgba(255, 0, 0, 0) 100%
                        );
                `;
            case 'red':
                return css`
                    background-image: radial-gradient(
                            46.5% 46% at 0% 57%,
                            #e74a4b 0%,
                            rgba(235, 67, 68, 0.83) 16.67%,
                            rgba(239, 60, 61, 0.67) 33.33%,
                            rgba(244, 52, 53, 0.5) 50%,
                            rgba(247, 41, 42, 0.33) 66.67%,
                            rgba(255, 0, 0, 0) 100%
                        ),
                        radial-gradient(
                            57.5% 76% at 100% 0%,
                            #ff0000 0%,
                            rgba(255, 0, 0, 0.83) 16.67%,
                            rgba(255, 0, 0, 0.67) 33.33%,
                            rgba(255, 0, 0, 0.5) 50%,
                            rgba(255, 0, 0, 0.33) 66.67%,
                            rgba(255, 0, 0, 0) 100%
                        );
                `;
            case 'purple':
                return css`
                    background-image: radial-gradient(
                            46.5% 46% at 0% 57%,
                            #a44be9 0%,
                            rgba(195, 52, 220, 0.83) 16.67%,
                            rgba(210, 45, 198, 0.67) 33.33%,
                            rgba(222, 37, 177, 0.5) 50%,
                            rgba(233, 28, 152, 0.33) 66.67%,
                            rgba(255, 0, 0, 0) 100%
                        ),
                        radial-gradient(
                            57.5% 76% at 100% 0%,
                            #6101ff 0%,
                            rgba(153, 0, 208, 0.83) 16.67%,
                            rgba(178, 0, 185, 0.67) 33.33%,
                            rgba(198, 0, 168, 0.5) 50%,
                            rgba(216, 0, 148, 0.33) 66.67%,
                            rgba(255, 0, 0, 0) 100%
                        );
                `;
        }
    }}
`;

/* Expired Card
----------------------------------*/

const cryingFaceAnimation = keyframes`
    0% { transform: translateX(-15px); }
    20% { transform: translateX(12px); }
    40% { transform: translateX(-9px); }
    60% { transform: translateX(6px); }
    80% { transform: translateX(-3px); }
    100% { transform: translateX(0); }
`;

export const ExpiredCard = styled(motion.div)`
    padding: 20px;

    position: absolute;
    inset: 0;
    z-index: 1;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;

    border-radius: 15px;

    background: linear-gradient(71.73deg, rgba(191, 41, 41, 0.15) 5.83%, rgba(255, 255, 255, 0.05) 92.02%);
`;

export const ExpiredIcon = styled(motion.div)`
    background-image: url(${CryingFace});
    width: 48px;
    height: 48px;

    animation: ${cryingFaceAnimation} 1s ease-in-out;
    transform-origin: center center;
`;

export const ExpiredTitle = styled(motion.div)`
    font-weight: 700;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: -0.03em;
    color: #ffffff;
    padding-bottom: 5px;
`;

/* Offer Card
----------------------------------*/

export const NewCard = styled(motion.div)`
    padding: 20px;
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    //gap: 10px;

    border-radius: 15px;

    background: linear-gradient(
        251.16deg,
        rgba(255, 136, 69, 0.15) 4.39%,
        rgba(255, 255, 255, 0.05) 52.33%,
        rgba(255, 123, 49, 0.25) 95.89%
    );

    ${EthIcon} {
        width: 22px;
    }

    ${ValueGroup} {
        display: flex;
        gap: 10px;
        align-items: center;
    }
`;

export const OfferTitle = styled(motion.div)`
    font-weight: 700;
    font-size: 14px;
    line-height: 100%;
    letter-spacing: -0.03em;
    color: #ffffff;
    padding-bottom: 4px;
`;

export const AcceptedCard = styled(NewCard)`
    & {
        background: linear-gradient(
            251.16deg,
            rgba(173, 69, 255, 0.15) 4.39%,
            rgba(255, 255, 255, 0.05) 52.33%,
            rgba(152, 49, 255, 0.25) 95.89%
        );
    }
`;
